import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { BarChart3, CalendarCog, CreditCard, FileText, Handshake, Landmark, LayoutDashboard, LogOut, Mail, Menu, MonitorPlay, Plug, Settings, Ticket, Users, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSite } from "../../context/SiteContext";
import { Logo } from "../site/Logo";
import { humanize } from "../../lib/format";
import { MediaHealthBanner } from "./MediaHealthBanner";
import { ModeButtons } from "./ModeButtons";

export const ADMIN_NAV = [
  ["", "Dashboard", LayoutDashboard], ["registrations", "Registrations", Users], ["payments", "Payments", CreditCard],
  ["sponsors", "Sponsors", Handshake], ["invitations", "Invitations", Ticket], ["virtual", "Virtual Attendance", MonitorPlay], ["communications", "Email Communications", Mail],
  ["reports", "Reports", BarChart3], ["event", "Event Setup", CalendarCog], ["content", "Website Content", FileText], ["users", "Settings / Users", Settings],
  ["integrations", "Integrations", Plug, "super_admin"],
  ["sag-fees", "SAG Processing Fees", Landmark, "finance"],
];
// sag_finance sees only the SAG module; "finance" items need Super Admin, Viewer (read-only) or an Editor with the finance grant.
const showItem = (u, need, path) => (u?.role === "sag_finance" ? path === "sag-fees"
  : !need || u?.role === need || (need === "finance" && (["super_admin", "viewer"].includes(u?.role) || u?.can_manage_finance)));

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { event, operating_mode } = useSite();
  const live = operating_mode === "live";
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen bg-[#F6F8FB]">
      <aside className={`on-dark fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-navy text-white transition-transform duration-200 lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <div className="flex items-center justify-between px-5 py-5"><Link to="/admin" aria-label="Admin dashboard"><Logo size="sm" /></Link>
          <button type="button" className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close sidebar"><X /></button></div>
        <nav aria-label="Admin" className="flex-1 space-y-1 px-3">
          {ADMIN_NAV.filter(([path, , , need]) => showItem(user, need, path)).map(([path, label, Ico]) => (
            <NavLink key={path} to={`/admin${path ? `/${path}` : ""}`} end={!path} onClick={() => setOpen(false)} data-testid={`admin-sidebar-${path || "dashboard"}-link`}
              className={({ isActive }) => `flex items-center gap-3 rounded px-3 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-burgundy font-semibold" : "text-white/85 hover:bg-white/10"}`}>
              <Ico size={18} aria-hidden="true" />{label}
            </NavLink>
          ))}
        </nav>
        <p className="px-6 py-6 text-xs tracking-[0.3em] text-white/70">A BRIGHTER<br />TOMORROW</p>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} aria-hidden="true" />}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3">
          <div className="flex items-center gap-3">
            <button type="button" className="lg:hidden" onClick={() => setOpen(true)} aria-label="Open sidebar" data-testid="admin-sidebar-toggle"><Menu /></button>
            <span className="hidden text-sm text-slate-600 sm:inline">{event.name}</span>
            <span className={`rounded px-2 py-0.5 text-xs font-bold ${live ? "bg-green-700 text-white" : "bg-amber-400 text-navy"}`} data-testid="admin-mode-badge">{live ? "LIVE" : "DEMO MODE"}</span>
            <span className="hidden text-xs text-slate-600 md:inline" data-testid="admin-mode-note">{live ? "Showing live records only" : "Showing demo records only"}</span>
          </div>
          <div className="flex items-center gap-4">
            {user.role === "super_admin" && <ModeButtons live={live} canSwitch />}
            <Link to="/" target="_blank" className="text-sm text-navy underline-offset-4 hover:underline" data-testid="admin-view-site-link">View Site</Link>
            <span className="flex items-center gap-2 text-sm" data-testid="admin-user-info">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-xs font-bold text-white" aria-hidden="true">{(user.name || user.email).slice(0, 2).toUpperCase()}</span>
              <span className="hidden sm:block"><b className="block leading-tight text-navy">{user.name || user.email}</b><span className="text-xs text-slate-600">{humanize(user.role)}</span></span>
            </span>
            <button type="button" onClick={logout} className="text-slate-600 hover:text-burgundy" aria-label="Log out" data-testid="admin-logout-btn"><LogOut size={18} /></button>
          </div>
        </header>
        <MediaHealthBanner />
        <main className="flex-1 p-5 lg:p-8" id="main"><Outlet /></main>
      </div>
    </div>
  );
}
